// Pack lifecycle endpoints — submit, fork, delete, promote, rate, etc.
// Mirrors the prod app's `app/frontend/src/lib/api.js` pack helpers so
// Profiles / PackEditor / Curator can run the same workflow as prod.

import { apiCall, apiSoft } from "@/lib/api/http";

export interface PackReview {
  id: string;
  pack_id: string;
  user_email: string;
  review_text: string;
  created_at: string;
}

export interface PackSubmitResponse {
  id: string;
  status: string;
  message?: string;
}

export interface PackForkResponse {
  id: string;
  name: string;
  status: "draft";
  forked_from?: string;
}

export const packsApi = {
  submit: (id: string): Promise<PackSubmitResponse> =>
    apiCall<PackSubmitResponse>(
      `/api/packs/${encodeURIComponent(id)}/submit`,
      { method: "POST" },
    ),

  fork: (id: string): Promise<PackForkResponse> =>
    apiCall<PackForkResponse>(
      `/api/packs/${encodeURIComponent(id)}/fork`,
      { method: "POST" },
    ),

  remove: (id: string): Promise<void> =>
    apiCall<void>(
      `/api/packs/${encodeURIComponent(id)}`,
      { method: "DELETE" },
    ),

  // Skip-review promotion — admin/curator only on backend; frontend
  // just calls and surfaces 403 if the caller isn't allowed.
  promote: (
    id: string,
    target: "approved" | "featured",
  ): Promise<{ id: string; status: string }> =>
    apiCall<{ id: string; status: string }>(
      `/api/packs/${encodeURIComponent(id)}/promote?target_status=${target}`,
      { method: "POST" },
    ),

  rate: (id: string, rating: number): Promise<{ id: string; rating: number }> =>
    apiCall(`/api/packs/${encodeURIComponent(id)}/rate`, {
      method: "POST",
      body: { rating },
    }),

  reviews: (id: string): Promise<PackReview[] | null> =>
    apiSoft<PackReview[]>(`/api/packs/${encodeURIComponent(id)}/reviews`),

  postReview: (id: string, review_text: string): Promise<PackReview> =>
    apiCall<PackReview>(`/api/packs/${encodeURIComponent(id)}/reviews`, {
      method: "POST",
      body: { review_text },
    }),

  uploadLogo: async (
    id: string,
    file: File,
  ): Promise<{ id: string; logo_url: string }> => {
    const fd = new FormData();
    fd.append("file", file);
    const res = await fetch(
      `/api/packs/${encodeURIComponent(id)}/logo`,
      { method: "POST", body: fd, credentials: "include" },
    );
    if (!res.ok) {
      const detail = await res
        .json()
        .then((j) => (j as { detail?: string }).detail)
        .catch(() => undefined);
      throw new Error(detail ?? `Logo upload failed (${res.status})`);
    }
    return res.json() as Promise<{ id: string; logo_url: string }>;
  },
};
